import { useState } from "react";
import {
  useGroups,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  Group,
} from "@/lib/api/group";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlusIcon } from "lucide-react";
import { CreateGroupDialog } from "./create-group-dialog";
import { EditGroupDialog } from "./edit-group-dialog";
import { ConfirmActionDialog } from "../ui/confirm-action-dialog";
import { useToast } from "../ui/use-toast";
import { useAbility } from "@/lib/casl/ability-context";

export function GroupSettings() {
  const { toast } = useToast();
  const { data: groups, isLoading, isError } = useGroups(true); // Include user count
  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const deleteGroup = useDeleteGroup();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<Group | null>(null);

  const ability = useAbility();
  const canCreateGroups = ability.can("create", "groups");
  const canUpdateGroups = ability.can("update", "groups");
  const canDeleteGroups = ability.can("delete", "groups");

  const handleCreateGroup = (name: string) => {
    if (!canCreateGroups) return;

    createGroup.mutate(
      { name },
      {
        onSuccess: () => setIsCreateDialogOpen(false),
      },
    );
  };

  const handleUpdateGroup = (name: string, is_billable_group?: boolean) => {
    if (!canUpdateGroups) return;

    if (editingGroup) {
      updateGroup.mutate(
        { id: editingGroup.id, name, is_billable_group },
        {
          onSuccess: () => setEditingGroup(null),
        },
      );
    }
  };

  const handleDeleteGroup = () => {
    if (!canDeleteGroups) return;

    if (deletingGroup) {
      deleteGroup.mutate(deletingGroup.id, {
        onSuccess: () => setDeletingGroup(null),
        onError: (error) => {
          console.error("Failed to delete group:", error);
          toast({
            title: "Failed to delete group",
            description:
              (error as any)?.response?.data?.message || "Please try again.",
            variant: "destructive",
          });
        },
      });
    }
  };

  const extraRoles = Array.from(
    new Set(
      groups
        ?.flatMap((group) => Object.keys(group?.countsByRole ?? {}))
        ?.filter((role) => !["mentor", "mentee"].includes(role)) ?? [],
    ),
  );

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading groups</div>;

  return (
    <div className="mt-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Manage Groups</h2>
        {canCreateGroups && (
          <Button
            variant="default"
            className="bg-black hover:bg-gray-700"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <PlusIcon className="mr-2 size-4" aria-hidden="true" />
            Add Group
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-2/5">Name</TableHead>
            <TableHead className="w-1/5 text-center">Total</TableHead>
            <TableHead className="w-1/5 text-center">Teens</TableHead>
            <TableHead className="w-1/5 text-center">Mentors</TableHead>
            {extraRoles?.map((role) => (
              <TableHead key={role} className="w-1/5 text-center capitalize">
                {role}
              </TableHead>
            ))}
            <TableHead className="w-1/5 text-center">Billable</TableHead>
            <TableHead className="w-1/5 text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups?.map((group) => {
            const total = Object.values(group?.countsByRole || {})?.reduce(
              (prev, curr) => prev + curr,
              0,
            );

            return (
              <TableRow key={group.id}>
                <TableCell className="w-2/5">{group.name}</TableCell>
                <TableCell className="w-1/5 text-center">
                  {total ?? "N/A"}
                </TableCell>
                <TableCell className="w-1/5 text-center">
                  {group.countsByRole?.["mentee"] ?? "-"}
                </TableCell>
                <TableCell className="w-1/5 text-center">
                  {group.countsByRole?.["mentor"] ?? "-"}
                </TableCell>
                {extraRoles?.map((role) => (
                  <TableCell className="w-1/5 text-center" key={role}>
                    {group.countsByRole?.[role] ?? "-"}
                  </TableCell>
                ))}
                <TableCell className="w-1/5 text-center">
                  {group.is_billable_group ? "Yes" : "No"}
                </TableCell>
                <TableCell className="w-1/5">
                  <div className="flex justify-center space-x-2">
                    {canUpdateGroups && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingGroup(group)}
                      >
                        Edit
                      </Button>
                    )}
                    {canDeleteGroups && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeletingGroup(group)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {canCreateGroups && (
        <CreateGroupDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSubmit={handleCreateGroup}
          isLoading={createGroup.isPending}
        />
      )}
      {canUpdateGroups && (
        <EditGroupDialog
          group={editingGroup}
          onClose={() => setEditingGroup(null)}
          onSubmit={handleUpdateGroup}
          isLoading={updateGroup.isPending}
        />
      )}
      {canDeleteGroups && (
        <ConfirmActionDialog
          isOpen={!!deletingGroup}
          onClose={() => setDeletingGroup(null)}
          onConfirm={handleDeleteGroup}
          itemName={deletingGroup?.name || ""}
        />
      )}
    </div>
  );
}
